import { Injectable } from '@angular/core';
import {
    CanActivate,
    Router,
    ActivatedRouteSnapshot,
    RouterStateSnapshot,
} from '@angular/router';

import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

import { LoginService } from '@intranet/modules/login';

class AuthBase implements CanActivate {
    constructor(private loginService: LoginService, private router: Router) {}

    canActivateChild(
        childRoute: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | Observable<boolean> {
        return this.canActivate(childRoute, state);
    }

    roles: string[] = [];

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): boolean | Observable<boolean> {
        const url = state.url;
        // On a page refresh the session is still being restored (the app's
        // startup checkLogin) when the first page is guarded. Deciding then
        // sent signed-in users to /login and back, and the page they were on
        // only came back after a long detour. Wait for that check to finish
        // instead — then decide exactly as before (still /login if they turn
        // out not to be signed in). Capped, so a check that never finishes
        // can't leave the app hanging: it decides on what's known by then.
        if (!this.loginService.isLoginChecked) {
            return this.loginService.loginChecked$.pipe(
                timeout(AuthBase.LOGIN_CHECK_WAIT_MS),
                catchError(() => of(true)),
                map(() => this.checkLogin(url))
            );
        }
        return this.checkLogin(url);
    }

    private static readonly LOGIN_CHECK_WAIT_MS = 15000;

    checkLogin(url: string): boolean {
        if (this.loginService.checkAuth(this.roles)) {
            return true;
        }
        this.loginService.redirectUrl = url;
        this.router.navigate(['/login']);
        return false;
    }
}

@Injectable()
export class AuthGuard extends AuthBase {
    constructor(loginService: LoginService, router: Router) {
        super(loginService, router);
    }
}

@Injectable()
export class AuthGuardAdmin extends AuthBase {
    constructor(loginService: LoginService, router: Router) {
        super(loginService, router);
        this.roles = ['Admin'];
    }
}

@Injectable()
export class AuthGuardManager extends AuthBase {
    constructor(loginService: LoginService, router: Router) {
        super(loginService, router);
        this.roles = ['Manager'];
    }
}
@Injectable()
export class AuthGuardCalendar extends AuthBase {
    constructor(loginService: LoginService, router: Router) {
        super(loginService, router);
        this.roles = ['Calendar'];
    }
}
@Injectable()
export class AuthGuardFinance extends AuthBase {
    constructor(loginService: LoginService, router: Router) {
        super(loginService, router);
        this.roles = ['Finance'];
    }
}
@Injectable()
export class AuthGuardMonthEnd extends AuthBase {
    constructor(loginService: LoginService, router: Router) {
        super(loginService, router);
        this.roles = ['Month-End'];
    }
}
